export const JobType = {
  Fetch: 'FETCH',
  Generate: 'GENERATE',
  Save: 'SAVE'
} as const

export type JobType = (typeof JobType)[keyof typeof JobType]

export const JobStatus = {
  Queued: 'QUEUED',
  Running: 'RUNNING',
  Completed: 'COMPLETED',
  Failed: 'FAILED',
  Cancelled: 'CANCELLED'
} as const

export type JobStatus = (typeof JobStatus)[keyof typeof JobStatus]

/**
 * A durable unit of background work.
 *
 * The desktop app kept batch state in the renderer, so a reload lost it. Here
 * the queue owns it: reconnecting clients re-read the job and resume watching.
 */
export interface JobDTO {
  id: string
  siteId: string
  type: JobType
  status: JobStatus
  total: number
  completed: number
  failed: number
  /** Human-readable step, e.g. "Querying media library…". */
  message: string | null
  error: string | null
  cancelRequested: boolean
  createdAt: string
  startedAt: string | null
  finishedAt: string | null
  /** Position in the shared queue when QUEUED; 0 once running. */
  queuePosition?: number
}

export function isJobActive(job: Pick<JobDTO, 'status'>): boolean {
  return job.status === JobStatus.Queued || job.status === JobStatus.Running
}

export function jobProgressPct(job: Pick<JobDTO, 'total' | 'completed' | 'failed'>): number {
  if (job.total <= 0) return 0
  return Math.round(((job.completed + job.failed) / job.total) * 100)
}

export interface StartGenerateRequest {
  /** Image ids to process. Omit to mean "everything matching `filter`". */
  imageIds?: string[]
  /** Server-side selection when `imageIds` is absent. */
  filter?: string
  /** Re-run images that already have generated text. */
  regenerate?: boolean
}

export interface StartSaveRequest {
  /** Image ids to write. Omit to mean "all approved and not yet saved". */
  imageIds?: string[]
}
